/**
 * rx-store storefront tags in the CRM (internal admin only). A patient,
 * order or subscription that came through a creator storefront carries the
 * store's {id, slug, name}; house (System Labs) customers carry none and
 * show no badge.
 */
import { Store } from 'lucide-react';

import type { PatientStorefrontTag } from '@/types/clinicalAdmin';

/** A storefront named on a row: its own `storefront`, else its
 *  `storefront_id` (a column, or Stripe metadata) matched against the
 *  patient's stores. */
export function storefrontOf(
  row: {
    storefront?: PatientStorefrontTag | null;
    storefront_id?: unknown;
    metadata?: Record<string, unknown> | null;
  },
  known: PatientStorefrontTag[] = [],
): PatientStorefrontTag | null {
  if (row.storefront?.name) return row.storefront;
  // The backend sends {id, slug: null, name: null} when it could not name
  // the store (a failed lookup); the patient's stores may still name it.
  const id =
    typeof row.storefront?.id === 'string' && row.storefront.id
      ? row.storefront.id
      : typeof row.storefront_id === 'string'
        ? row.storefront_id
        : typeof row.metadata?.storefront_id === 'string'
          ? row.metadata.storefront_id
          : null;
  if (!id) return null;
  return known.find((store) => store.id === id) ?? row.storefront ?? null;
}

export function StoreBadge({ store }: { store: PatientStorefrontTag }) {
  // An unnamed store (the backend could not read it) still shows a badge.
  const label = store.name || store.slug || 'Storefront';
  return (
    <span
      className="inline-flex items-center gap-4 max-w-[16rem] type-10 rounded px-6 py-2 border border-line bg-page text-ink whitespace-nowrap"
      title={`rx-store storefront · ${store.slug || store.id}`}
    >
      <Store size={10} aria-hidden className="shrink-0 text-ink-muted" />
      <span className="truncate">{label}</span>
    </span>
  );
}

/** Every store a patient is tagged with; nothing for a house-only one. */
export function StoreBadges({ stores }: { stores: PatientStorefrontTag[] | null | undefined }) {
  if (!stores || stores.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-4">
      {stores.map((store) => (
        <StoreBadge key={store.id} store={store} />
      ))}
    </span>
  );
}
